import React, { useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Box, Container, Grid } from "@mui/material"
import * as Yup from "yup"
import {
  MuiPageHeader,
  MuiCard,
  MuiForm,
  MuiTextField,
  MuiSelect,
  MuiLoadingButton,
  MuiAlert,
} from "../../components/mui"
import { postMaterial } from "../../helpers/api_helper"

const MuiMaterialCreate = () => {
  const navigate = useNavigate()
  document.title = "Create Material | FlexFlow ERP"

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Initial form values
  const initialValues = {
    name: "",
    sku: "",
    category: "",
    costprice: "",
    stock: "",
    unit: "KG",
    vat: "",
    description: "",
  }

  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),
    sku: Yup.string(),
    category: Yup.string(),
    costprice: Yup.number()
      .typeError("Cost price must be a number")
      .positive("Cost price must be positive"),
    stock: Yup.number()
      .typeError("Stock must be a number")
      .min(0, "Stock cannot be negative")
      .integer("Stock must be a whole number"),
    unit: Yup.string().required("Unit is required"),
    vat: Yup.number()
      .typeError("VAT must be a number")
      .min(0, "VAT cannot be negative")
      .max(100, "VAT cannot exceed 100"),
    description: Yup.string(),
  })

  // Unit options
  const unitOptions = [
    { value: "KG", label: "Kilos" },
    { value: "Meter", label: "Meter" },
    { value: "CM", label: "Centimeters" },
    { value: "MM", label: "Millimeters" },
    { value: "Litter", label: "Litter" },
    { value: "CubicM", label: "Cubic Meter" },
    { value: "Item", label: "Item" },
  ]

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true)
    setError(null)

    const payload = {
      resource: [
        {
          name: values.name,
          sku: values.sku || null,
          category: values.category || null,
          description: values.description || null,
          vat: values.vat ? Number(values.vat) : null,
          costprice: values.costprice ? Number(values.costprice) : null,
          stock: values.stock ? Number(values.stock) : 0,
          unit: values.unit,
        },
      ],
    }

    try {
      const res = await postMaterial(payload)

      if (res && res.error) {
        setError(res.error.message || "Failed to create material")
        return
      }

      if (res && res.resource && res.resource[0]) {
        setSuccess(true)
        setTimeout(() => {
          navigate("/mui-materials-list")
        }, 1500)
      }
    } catch (err) {
      setError(err.message || "An error occurred while creating the material")
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    navigate("/mui-materials-list")
  }

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        <MuiPageHeader
          title="Create Material"
          breadcrumbs={[
            { label: "Materials", path: "/mui-materials-list" },
            { label: "Create Material" },
          ]}
        />

        {error && (
          <MuiAlert
            severity="error"
            onClose={() => setError(null)}
            sx={{ mb: 3 }}
          >
            {error}
          </MuiAlert>
        )}

        {success && (
          <MuiAlert severity="success" sx={{ mb: 3 }}>
            Material created successfully! Redirecting...
          </MuiAlert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <MuiCard title="Material Information" showDivider>
              <MuiForm
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                actionsPosition="right"
                actions={(formikProps) => (
                  <>
                    <MuiLoadingButton
                      variant="outlined"
                      color="inherit"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </MuiLoadingButton>
                    <MuiLoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={loading}
                      disabled={!formikProps.isValid || loading}
                    >
                      Create Material
                    </MuiLoadingButton>
                  </>
                )}
              >
                {({ values, errors, touched }) => (
                  <Box>
                    <Grid container spacing={2}>
                      {/* Name */}
                      <Grid item xs={12} md={6}>
                        <MuiTextField
                          name="name"
                          label="Name"
                          placeholder="Enter material name"
                          required
                        />
                      </Grid>

                      {/* SKU */}
                      <Grid item xs={12} md={6}>
                        <MuiTextField
                          name="sku"
                          label="SKU"
                          placeholder="Enter SKU code"
                        />
                      </Grid>

                      {/* Category */}
                      <Grid item xs={12} md={6}>
                        <MuiTextField
                          name="category"
                          label="Category"
                          placeholder="Enter category"
                        />
                      </Grid>

                      {/* Unit */}
                      <Grid item xs={12} md={6}>
                        <MuiSelect
                          name="unit"
                          label="Unit"
                          options={unitOptions}
                          required
                        />
                      </Grid>

                      {/* Cost Price */}
                      <Grid item xs={12} md={4}>
                        <MuiTextField
                          name="costprice"
                          label="Cost Price"
                          type="number"
                          placeholder="0.00"
                        />
                      </Grid>

                      {/* Stock */}
                      <Grid item xs={12} md={4}>
                        <MuiTextField
                          name="stock"
                          label="Stock"
                          type="number"
                          placeholder="0"
                        />
                      </Grid>

                      {/* VAT */}
                      <Grid item xs={12} md={4}>
                        <MuiTextField
                          name="vat"
                          label="VAT (%)"
                          type="number"
                          placeholder="0"
                        />
                      </Grid>

                      {/* Description */}
                      <Grid item xs={12}>
                        <MuiTextField
                          name="description"
                          label="Description"
                          placeholder="Enter material description"
                          multiline
                          rows={4}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </MuiForm>
            </MuiCard>
          </Grid>

          {/* Help/Info Panel */}
          <Grid item xs={12} lg={4}>
            <MuiCard title="Tips" variant="outlined">
              <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li style={{ marginBottom: 8 }}>
                    <strong>Name:</strong> Required. The display name for the material.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    <strong>SKU:</strong> Stock Keeping Unit - a unique identifier for tracking inventory.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    <strong>Unit:</strong> Select the measurement unit used for this material.
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    <strong>Cost Price:</strong> The purchase cost per unit.
                  </li>
                  <li>
                    <strong>Stock:</strong> Current inventory quantity.
                  </li>
                </ul>
              </Box>
            </MuiCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

MuiMaterialCreate.propTypes = {
  history: PropTypes.object,
}

export default MuiMaterialCreate
